import { Filter, JoinOperator } from '@app/components/query-filters/filters.types';
import generateRandomId from '@app/components/query-filters/helpers/randomId';
import { Select } from 'antd';
import dayjs from 'dayjs';
import React from 'react';
import { useTranslation } from 'react-i18next';

interface VisitsQuickFilterProps {
  onApplyFilters: (filters: Filter[], joinOperator: JoinOperator) => void;
}

type QuickFilterOption = 'all' | 'today' | 'this_week' | 'this_month' | 'this_year';

const VisitsQuickFilter: React.FC<VisitsQuickFilterProps> = ({ onApplyFilters }) => {
  const { t } = useTranslation();
  const [selectedFilter, setSelectedFilter] = React.useState<QuickFilterOption | undefined>(undefined);

  const getDateFilters = (option: QuickFilterOption): Filter[] => {
    const now = dayjs();

    switch (option) {
      case 'today': {
        const today = now.format('YYYY-MM-DD');
        return [
          {
            id: generateRandomId(),
            operator: '&&',
            rules: [
              {
                id: generateRandomId(),
                col: 'start_time',
                operator: '==',
                value: today,
                rules: [],
              },
            ],
          },
        ];
      }
      case 'this_week': {
        const startOfWeek = now.startOf('week').format('YYYY-MM-DD');
        const endOfWeek = now.endOf('week').format('YYYY-MM-DD');
        return [
          {
            id: generateRandomId(),
            operator: '&&',
            rules: [
              {
                id: generateRandomId(),
                col: 'start_time',
                operator: '>=',
                value: startOfWeek,
                rules: [],
              },
              {
                id: generateRandomId(),
                col: 'start_time',
                operator: '<=',
                value: endOfWeek,
                rules: [],
              },
            ],
          },
        ];
      }
      case 'this_month': {
        const startOfMonth = now.startOf('month').format('YYYY-MM-DD');
        const endOfMonth = now.endOf('month').format('YYYY-MM-DD');
        return [
          {
            id: generateRandomId(),
            operator: '&&',
            rules: [
              {
                id: generateRandomId(),
                col: 'start_time',
                operator: '>=',
                value: startOfMonth,
                rules: [],
              },
              {
                id: generateRandomId(),
                col: 'start_time',
                operator: '<=',
                value: endOfMonth,
                rules: [],
              },
            ],
          },
        ];
      }
      case 'this_year': {
        const startOfYear = now.startOf('year').format('YYYY-MM-DD');
        const endOfYear = now.endOf('year').format('YYYY-MM-DD');
        return [
          {
            id: generateRandomId(),
            operator: '&&',
            rules: [
              {
                id: generateRandomId(),
                col: 'start_time',
                operator: '>=',
                value: startOfYear,
                rules: [],
              },
              {
                id: generateRandomId(),
                col: 'start_time',
                operator: '<=',
                value: endOfYear,
                rules: [],
              },
            ],
          },
        ];
      }
      default:
        return [];
    }
  };

  const handleFilterChange = (value: QuickFilterOption) => {
    setSelectedFilter(value);

    // Get new date filters based on selection
    const dateFilters = getDateFilters(value);

    onApplyFilters(dateFilters, '&&');
  };

  return (
    <Select
      value={selectedFilter}
      onChange={handleFilterChange}
      style={{ width: 200 }}
      options={[
        { label: t('visits-quick-filter-today'), value: 'today' },
        { label: t('visits-quick-filter-this-week'), value: 'this_week' },
        { label: t('visits-quick-filter-this-month'), value: 'this_month' },
        { label: t('visits-quick-filter-this-year'), value: 'this_year' },
      ]}
      placeholder={t('visits-quick-filter-placeholder')}
    />
  );
};

export default VisitsQuickFilter;
